import {
  Button,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Paper,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material'
import { 
  Search,
  Add,
  Edit,
  Delete,
  FilterList,
  ExpandMore,
  Email,
  Language,
  Business,
  Public
} from '@mui/icons-material'
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { useState } from 'react'

import { NotificationMessageTemplate } from '../../../services/Hub/notificationCenter/types'

type Props = {
  messageTemplates: NotificationMessageTemplate[]
  isLoading?: boolean
  onCreateTemplate?: () => void
  onEditTemplate?: (template: NotificationMessageTemplate) => void
  onDeleteTemplate?: (template: NotificationMessageTemplate) => void
}

export default function TemplatesTab({
  messageTemplates,
  isLoading = false,
  onCreateTemplate,
  onEditTemplate,
  onDeleteTemplate
}: Props) {
  const [filters, setFilters] = useState({
    organization: '',
    includeGlobal: true,
    channel: '',
    name: ''
  })

  // Filter templates based on all filter criteria
  const filteredTemplates = messageTemplates.filter(template => {
    const matchesName = !filters.name || template.name.toLowerCase().includes(filters.name.toLowerCase())
    const matchesChannel = !filters.channel || template.channel === filters.channel
    const matchesOrganization = !filters.organization || 
      (filters.organization === 'global' ? !template.ownerOrganizationId : template.ownerOrganizationId)
    const matchesGlobalFilter = filters.includeGlobal || template.ownerOrganizationId
    
    return matchesName && matchesChannel && matchesOrganization && matchesGlobalFilter
  })

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'CUSTOMER_IO':
        return '📧'
      case 'EMAIL':
        return '✉️'
      default:
        return '📱'
    }
  }

  const columns: GridColDef<NotificationMessageTemplate>[] = [
    {
      field: 'name',
      headerName: 'Nome',
      flex: 2,
      minWidth: 300,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Template #{params.row.id}
          </Typography>
        </Box>
      )
    },
    {
      field: 'channel',
      headerName: 'Canal',
      width: 140,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.value}
          icon={<span>{getChannelIcon(params.value)}</span>}
          variant="outlined"
          size="small"
          color="primary"
        />
      )
    },
    {
      field: 'ownerOrganizationId',
      headerName: 'Disponível para',
      width: 160,
      renderCell: (params: GridRenderCellParams) => {
        const isGlobal = !params.value
        return (
          <Chip
            icon={isGlobal ? <Public /> : <Business />}
            label={isGlobal ? 'Global' : 'Organização'}
            color={isGlobal ? 'success' : 'info'}
            variant={isGlobal ? 'filled' : 'outlined'}
            size="small"
          />
        )
      }
    },
    {
      field: 'createdAt',
      headerName: 'Criado em',
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        const date = params.value ? new Date(params.value) : null
        return (
          <Typography variant="body2" color="text.secondary">
            {date ? date.toLocaleDateString('pt-BR') : 'N/A'}
          </Typography>
        )
      }
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 120,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Editar">
            <IconButton 
              size="small" 
              onClick={() => onEditTemplate?.(params.row)}
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton 
              size="small" 
              onClick={() => onDeleteTemplate?.(params.row)}
              sx={{ 
                bgcolor: 'error.main', 
                color: 'white',
                '&:hover': { bgcolor: 'error.dark' }
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ]

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="flex-start" 
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Templates de Mensagem
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gerencie templates de mensagem para diferentes canais de comunicação.
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={onCreateTemplate}
          disabled={!onCreateTemplate}
          size="large"
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          Novo Template
        </Button>
      </Stack>

      {/* Enhanced Filters Section */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FilterList />
              <Typography variant="subtitle1" fontWeight={600}>
                Filtros Avançados
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              {/* First Row */}
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Organização</InputLabel>
                  <Select
                    value={filters.organization}
                    label="Organização"
                    onChange={e => setFilters(prev => ({ ...prev, organization: e.target.value }))}>
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="global">Global</MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel>Canal</InputLabel>
                  <Select
                    value={filters.channel}
                    label="Canal"
                    onChange={e => setFilters(prev => ({ ...prev, channel: e.target.value }))}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="CUSTOMER_IO">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <span>📧</span>
                        <span>Customer.io</span>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="EMAIL">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <span>✉️</span>
                        <span>Email</span>
                      </Stack>
                    </MenuItem>
                  </Select>
                </FormControl>

                <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filters.includeGlobal}
                        onChange={e => setFilters(prev => ({ ...prev, includeGlobal: e.target.checked }))}
                        color="primary"
                      />
                    }
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Public fontSize="small" />
                        <Typography>Incluir templates globais</Typography>
                      </Stack>
                    }
                  />
                </Card>
              </Stack>

              {/* Second Row */}
              <TextField
                label="Buscar por nome"
                value={filters.name}
                onChange={e => setFilters(prev => ({ ...prev, name: e.target.value }))}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                placeholder="Digite o nome do template..."
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Card>

      {/* Stats Section */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
          <CardContent sx={{ py: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'info.light' }}>
                <Email />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Total de Templates
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {filteredTemplates.length}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        
        <Card variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
          <CardContent sx={{ py: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'success.light' }}>
                <Public />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Templates Globais
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {filteredTemplates.filter(t => !t.ownerOrganizationId).length}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Data Grid */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <DataGrid
          rows={filteredTemplates}
          columns={columns}
          loading={isLoading}
          getRowId={row => row.id}
          disableRowSelectionOnClick
          rowHeight={60}
          sx={{ 
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'grey.50',
              borderBottom: '2px solid',
              borderColor: 'divider'
            },
            '& .MuiDataGrid-cell': {
              borderColor: 'grey.100',
              py: 1
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }
          }}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 }
            }
          }}
        />
      </Card>
    </Box>
  )
}
